import React, { Fragment, useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import { Fade, Tooltip, Chip } from '@mui/material';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';

const IncomeExpenseDetails = () => {
    const [mounted, setMounted] = useState(false);
    const { id } = useParams();
    const { isAuthenticated, companyId, userId, token, logout } = useAuth();
    const [formData, setFormData] = useState({});

    useEffect(() => {
        setMounted(true);
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    // Add a function to format the date
    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        return formattedDate;
    };

    useEffect(() => {
        fetchData('IncomeExpenseApi', `/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    const invoiceData = response.response_data;
                    setFormData({
                        ...invoiceData,
                        UpdatedBy: userId,
                    });
                }
            })
            .catch((error) => {
                console.error('Error fetching Invoice Details:', error);
            });
    }, [id, token, userId]);

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '4px',
                position: 'relative'
            }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .section-card {
                        animation: slideIn 0.6s ease-out forwards;
                        opacity: 0;
                    }
                    .section-card:nth-child(1) { animation-delay: 0.1s; }
                    .section-card:nth-child(2) { animation-delay: 0.2s; }
                `}</style>

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '6px',
                    position: 'relative'
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '4px',
                        paddingBottom: '3px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                padding: '6px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)'
                            }}>
                                <VisibilityIcon style={{ fontSize: '18px', color: 'white' }} />
                            </div>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <h1 style={{
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        margin: 0,
                                        letterSpacing: '-0.3px'
                                    }}>
                                        Income & Expense Details
                                    </h1>
                                    <Tooltip title="View transaction details" arrow placement="right">
                                        <InfoIcon style={{ fontSize: '14px', color: '#667eea', cursor: 'pointer' }} />
                                    </Tooltip>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '1px' }}>
                                    <p style={{ margin: 0, color: '#666', fontSize: '10px' }}>
                                        View income or expense transaction information
                                    </p>
                                    <Chip
                                        icon={<CheckCircleIcon style={{ fontSize: '12px' }} />}
                                        label="Read Only"
                                        size="small"
                                        style={{
                                            height: '18px',
                                            fontSize: '9px',
                                            fontWeight: '600',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Income/Expense Display - Centered */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            marginTop: '0px',
                            marginBottom: '0px'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                gap: '3px', 
                                alignItems: 'center',
                                background: 'rgba(255, 255, 255, 0.9)',
                                padding: '3px',
                                borderRadius: '20px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                border: '1px solid rgba(102, 126, 234, 0.15)'
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '5px',
                                    padding: '4px 14px',
                                    borderRadius: '18px',
                                    background: formData.IsType === true 
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                                        : 'transparent',
                                    boxShadow: formData.IsType === true 
                                        ? '0 3px 10px rgba(102, 126, 234, 0.35)' 
                                        : 'none'
                                }}>
                                    <div style={{
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        border: formData.IsType === true 
                                            ? '2px solid white' 
                                            : '2px solid #667eea',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: formData.IsType === true 
                                            ? 'white' 
                                            : 'transparent'
                                    }}>
                                        {formData.IsType === true && (
                                            <div style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                            }} />
                                        )}
                                    </div>
                                    <span style={{ 
                                        fontSize: '11px', 
                                        color: formData.IsType === true ? 'white' : '#667eea', 
                                        fontWeight: '700',
                                        letterSpacing: '0.2px'
                                    }}>
                                        Income
                                    </span>
                                </div>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '5px',
                                    padding: '4px 14px',
                                    borderRadius: '18px',
                                    background: formData.IsType === false 
                                        ? 'linear-gradient(135deg, #ee5a6f 0%, #f29263 100%)' 
                                        : 'transparent',
                                    boxShadow: formData.IsType === false 
                                        ? '0 3px 10px rgba(238, 90, 111, 0.35)' 
                                        : 'none'
                                }}>
                                    <div style={{
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        border: formData.IsType === false 
                                            ? '2px solid white' 
                                            : '2px solid #ee5a6f',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: formData.IsType === false 
                                            ? 'white' 
                                            : 'transparent'
                                    }}>
                                        {formData.IsType === false && (
                                            <div style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #ee5a6f 0%, #f29263 100%)'
                                            }} />
                                        )}
                                    </div>
                                    <span style={{ 
                                        fontSize: '11px', 
                                        color: formData.IsType === false ? 'white' : '#ee5a6f', 
                                        fontWeight: '700',
                                        letterSpacing: '0.2px'
                                    }}>
                                        Expense
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <Tooltip title="Return to income/expense list" arrow>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to={{
                                        pathname: "/IncomeExpenseIndex",
                                        state: { from: "/IncomeExpenseDetails" },
                                    }}
                                    onClick={() => {
                                        localStorage.setItem("shouldReload", "true");
                                    }}
                                    startIcon={<ArrowBackIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        borderColor: '#667eea',
                                        color: '#667eea',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        borderWidth: '1.5px',
                                        minHeight: '28px',
                                        textTransform: 'none',
                                        fontWeight: '600'
                                    }}
                                >
                                    Back to List
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Transaction Details Section */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <AccountBalanceWalletIcon style={{ color: '#667eea', fontSize: '16px' }} />
                        <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#2d3748' }}>
                            Transaction Details
                        </h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '6px' }}>
                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Account Head
                            </label>
                            <input
                                value={formData.AccountHeadName || ''}
                                type="text"
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Date
                            </label>
                            <input
                                type="date"
                                value={formatDateForInput(formData.BillDate)}
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Bill Number
                            </label>
                            <input
                                type="text"
                                value={`${formData.BillNo || ''}/${formData.FinYear || ''}`}
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                GST %
                                <input
                                    type="checkbox"
                                    checked={formData.IsIGST || false}
                                    disabled
                                    style={{ cursor: 'not-allowed' }}
                                />
                                <span style={{ fontSize: '10px', color: '#e53e3e' }}>IGST</span>
                            </label>
                            <select
                                value={formData.GSTRate || 0}
                                disabled
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            >
                                <option value={0}>0</option>
                                <option value={3}>3</option>
                                <option value={5}>5</option>
                                <option value={12}>12</option>
                                <option value={18}>18</option>
                                <option value={28}>28</option>
                            </select>
                        </div>
                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Amount
                            </label>
                            <input
                                type="number"
                                value={formData.Amount || ''}
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500',
                                    textAlign: 'right'
                                }}
                            />
                        </div>
                    </div>

                    {/* GST Summary */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr',
                        gap: '6px',
                        marginTop: '6px',
                        padding: '4px 8px',
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 126, 234, 0.15)',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <label style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                color: '#4a5568',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                whiteSpace: 'nowrap'
                            }}>
                                Remarks:
                            </label>
                            <input
                                type="text"
                                value={formData.Remarks || ''}
                                readOnly
                                style={{
                                    flex: 1,
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            />
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '2px 8px',
                            background: 'white',
                            borderRadius: '6px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                            height: '28px'
                        }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                CGST:
                            </span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#e53e3e' }}>
                                {formData.CGSTAmount || '0.00'}
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '2px 8px',
                            background: 'white',
                            borderRadius: '6px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                            height: '28px'
                        }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                SGST:
                            </span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#e53e3e' }}>
                                {formData.SGSTAmount || '0.00'}
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '2px 8px',
                            background: 'white',
                            borderRadius: '6px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                            height: '28px'
                        }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                IGST:
                            </span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#e53e3e' }}>
                                {formData.IGSTAmount || '0.00'}
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '2px 8px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '6px',
                            height: '28px',
                            boxShadow: '0 3px 8px rgba(102, 126, 234, 0.3)'
                        }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                Total:
                            </span>
                            <span style={{ fontSize: '12px', fontWeight: '800', color: 'white' }}>
                                {formData.TotalAmount || '0.00'}
                            </span>
                        </div>
                    </div>

                    {/* Payment Details Section */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', marginTop: '6px' }}>
                        <AccountBalanceWalletIcon style={{ color: '#667eea', fontSize: '16px' }} />
                        <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#2d3748' }}>
                            Payment Details
                        </h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '6px' }}>
                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Payment Mode
                            </label>
                            <input
                                value={formData.PaymentName || ''}
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Bank
                            </label>
                            <input
                                value={formData.BankName || ''}
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Reference No
                            </label>
                            <input
                                value={formData.ChequeNumber || ''}
                                type="text"
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Reference Date
                            </label>
                            <input
                                value={formatDateForInput(formData.ChequeDate)}
                                type="date"
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                display: 'block'
                            }}>
                                Reference Details
                            </label>
                            <input
                                value={formData.chequeDetails || ''}
                                type="text"
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#fafafa',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: '500'
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    fontFamily: 'serif',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '13px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    zIndex: 1000
                }}>
                    <CopyrightIcon style={{ fontSize: '18px', color: '#667eea' }} />
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
            </div>
        </Fade>
    );
};

export default IncomeExpenseDetails;
